import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatActualTarget,
  formatTargetMetricValue,
  type UnitsPreference,
  type WeeklyTargetRow,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { IncompleteNote, incompleteWord } from "./IncompleteNote";
import { makeMeterRowShellStyle } from "./meterSharedStyles";
import {
  NO_COVERAGE_GLYPH,
  TargetBar,
  ZONE_GLYPH,
  noCoverageWord,
  zoneTextStyle,
  zoneWord,
} from "./TargetBar";
import { weightUnitLabels } from "../weightUnitLabels";

// <TargetMeterRow> — one row of a target meter: the label, the actual/target
// value pair, the zone word+glyph, and the <TargetBar> underneath. Lifted at
// AP-3b's 2nd callsite (the in-workout strip) out of Home/WeeklyTargetsCard so
// the weekly Home card and the in-workout strip render an identical row from
// one place. The caller resolves the label (muscle name vs exercise name) and
// passes the row; everything else — value formatting, the never-colour-only
// zone glyph+word, the composed screen-reader label, the bar — lives here.

export type TargetMeterRowProps = {
  theme: UiTheme;
  row: WeeklyTargetRow;
  // Caller-resolved display label (muscleKey(...) for a muscle row, the
  // catalog name for an exercise row) — the primitive is axis-agnostic.
  label: string;
  units: UnitsPreference;
  locale: string;
  // Scale for an untargeted (neutral) bar — the caller's busiest untargeted
  // row. Ignored for targeted rows (they scale to their own goal).
  degradedMax?: number;
};

export function TargetMeterRow({
  theme,
  row,
  label,
  units,
  locale,
  degradedMax,
}: TargetMeterRowProps) {
  const { t } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const styles = makeStyles(theme);
  const { actual: actualStr, target: targetStr } = formatActualTarget(
    row,
    units,
    locale,
    unitLabels,
  );
  // The row's state word: "no coverage" (AP-9 — program prescribes ~0 for this
  // target) wins over the raw zone, so the screen reader and the sighted glance
  // hear the same distinction. Falls back to the zone word for a scored row.
  const stateWord = row.noCoverage
    ? noCoverageWord(t)
    : row.zone != null
      ? zoneWord(t, row.zone)
      : null;
  // ME-6 — the program's prescribed value for this muscle (2nd reference).
  // programWeeklyPrescription yields a HARD-SET count (a credit-weighted cycle
  // average, so a fractional float like 7.333…), so it's only meaningful on a
  // "hard-sets" row — a tonnage/reps target would misrender a set count in the
  // wrong unit, so those rows show no 2nd reference at all (label AND bar mark).
  // The in-workout (exercise-axis) rows never populate `prescribed` either.
  const prescribedValue =
    !row.noCoverage &&
    row.metric === "hard-sets" &&
    row.prescribed != null &&
    row.prescribed > 0
      ? row.prescribed
      : null;
  // Format through the row's own formatter (never the raw float) so "plan 7.3"
  // reads like the "8.4 / 12" value pair above it, locale-grouped, ≤1dp.
  const prescribedLabel =
    prescribedValue != null
      ? t("mobile.home.targetsPrescribed", {
          n: formatTargetMetricValue(
            prescribedValue,
            row.metric,
            units,
            locale,
            unitLabels,
          ),
          defaultValue: "plan {{n}}",
        })
      : null;
  // One composed label so a screen reader announces the row as a single unit,
  // and — when there's a prescription — hears all THREE references it shows:
  // done ("8 / 12"), plan ("plan 10"), goal-state ("building").
  const a11yLabel = [
    label,
    targetStr != null ? `${actualStr} / ${targetStr}` : actualStr,
    prescribedLabel,
    stateWord,
    row.incomplete ? incompleteWord(t) : null,
  ]
    .filter(Boolean)
    .join(", ");
  return (
    <View>
      <View accessible accessibilityLabel={a11yLabel}>
        <View style={styles.rowTop}>
          <Text style={styles.pattern} numberOfLines={1}>
            {label}
          </Text>
          <View style={styles.valueWrap}>
            <Text style={styles.value}>
              {actualStr}
              {targetStr != null ? (
                <Text style={styles.valueTarget}>
                  {" / "}
                  {targetStr}
                </Text>
              ) : null}
            </Text>
            {row.noCoverage ? (
              <Text style={[styles.state, styles.stateNoCoverage]}>
                {NO_COVERAGE_GLYPH} {noCoverageWord(t)}
              </Text>
            ) : row.zone != null ? (
              <Text style={[styles.state, zoneTextStyle(theme, row.zone)]}>
                {ZONE_GLYPH[row.zone]} {zoneWord(t, row.zone)}
              </Text>
            ) : null}
          </View>
        </View>
        <TargetBar
          theme={theme}
          actual={row.actual}
          target={row.target}
          bandLow={row.bandLow}
          bandHigh={row.bandHigh}
          zone={row.zone}
          degradedMax={degradedMax}
          noCoverage={row.noCoverage}
          prescribed={prescribedValue}
        />
        {/* #129: its own line under the bar, so a narrow screen never wraps
            "plan N" away from its number inside the value cluster. */}
        {prescribedLabel ? (
          <Text style={styles.prescribed}>{prescribedLabel}</Text>
        ) : null}
      </View>
      {/* Sibling of the accessible block: its pill must stay reachable. */}
      {row.incomplete ? <IncompleteNote theme={theme} reasons={row.incompleteReasons} /> : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: ty } = theme;
  return StyleSheet.create({
    // Shell da meterSharedStyles, una sola definizione: era condivisa con la
    // riga della banda (tolta in PG-64 fetta 2), e le righe di misura devono
    // leggersi come lo stesso oggetto.
    ...makeMeterRowShellStyle(theme),
    pattern: {
      flex: 1,
      minWidth: 0,
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "500",
    },
    valueTarget: { color: p.ink3 },
    // ME-6 — the "plan N" 2nd-reference label, matching the dashed mark's quiet
    // ink2 shade (the mockup's blue `info` is deferred to the theming track).
    // Tabular numerals so the count aligns with the value row above the bar.
    prescribed: {
      marginTop: theme.spacing.s1,
      fontSize: ty.sizes.xs,
      fontWeight: "600",
      color: p.ink2,
      fontVariant: ["tabular-nums"],
    },
    // "no coverage" reads at ink2 — one weight louder than "building" (ink3), so
    // the meter's most-different state gets its own identity instead of looking
    // like a shade of under. The glyph + word still carry it; the colour only
    // reinforces (never colour-alone).
    stateNoCoverage: { color: p.ink2 },
  });
}
